import { type VaultTreeNode } from "../model/types.ts";
import { type VaultAdapter } from "./VaultAdapter.ts";
import * as fs from "./fsAccess.ts";
import { type TrashEntry } from "./trash.ts";

/** A vault backed by a real on-disk folder via the File System Access API. */
export class FileSystemVaultAdapter implements VaultAdapter {
  readonly name: string;

  /**
   * Tail of the mutation queue. Nothing in this API is transactional, and a
   * rename is a copy and a delete with a long gap in between: let a second
   * write land in that gap — an autosave firing while a folder is being
   * renamed, two clicks on the same row — and the two interleave over the same
   * bytes. A real file manager serialises; so does this.
   *
   * Reads deliberately stay off the queue. They can't corrupt anything, and
   * parking a document open behind a multi-second folder copy would trade a
   * rare race for a freeze on every rename.
   */
  private queue: Promise<unknown> = Promise.resolve();

  constructor(private root: FileSystemDirectoryHandle) {
    this.name = root.name;
  }

  /** Run `job` once every mutation queued before it has settled. */
  private enqueue<T>(job: () => Promise<T>): Promise<T> {
    // `then(job, job)` so a rejected predecessor still lets the next one run:
    // one failed rename must not wedge every write that follows it.
    const run = this.queue.then(job, job);
    this.queue = run.catch(() => {});
    return run;
  }

  tree(): Promise<VaultTreeNode[]> {
    return fs.walkTree(this.root);
  }

  read(path: string): Promise<string> {
    return fs.readFile(this.root, path);
  }

  write(path: string, contents: string): Promise<void> {
    return this.enqueue(() => fs.writeFile(this.root, path, contents));
  }

  create(path: string, contents: string): Promise<string> {
    return this.enqueue(() => fs.createFile(this.root, path, contents));
  }

  createFolder(path: string): Promise<string> {
    return this.enqueue(() => fs.createFolder(this.root, path));
  }

  readBinary(path: string): Promise<Blob> {
    return fs.readBinaryFile(this.root, path);
  }

  createBinary(path: string, data: Blob): Promise<string> {
    return this.enqueue(() => fs.createBinaryFile(this.root, path, data));
  }

  rename(from: string, to: string): Promise<void> {
    return this.enqueue(() => fs.renameFile(this.root, from, to));
  }

  remove(path: string): Promise<void> {
    return this.enqueue(() => fs.trashFile(this.root, path));
  }

  purge(path: string): Promise<void> {
    return this.enqueue(() => fs.deleteFile(this.root, path));
  }

  trashList(): Promise<TrashEntry[]> {
    return this.enqueue(() => fs.readTrashIndex(this.root));
  }

  trashRestore(id: string): Promise<string> {
    return this.enqueue(() => fs.restoreTrashEntry(this.root, id));
  }

  trashPurge(id: string): Promise<void> {
    return this.enqueue(() => fs.purgeTrashEntry(this.root, id));
  }

  trashEmpty(): Promise<void> {
    return this.enqueue(() => fs.emptyTrash(this.root));
  }

  pruneEmptyDirs(path: string): Promise<void> {
    return this.enqueue(() => fs.pruneEmptyDirs(this.root, path));
  }
}
